import log from 'loglevel'
import {
  addLogRecord,
  clearLogRecords,
  createStoredLogRecord,
  deleteRecords,
  getSendableBatch,
  restoreFailedRecords
} from './loggerStorage'
import type { LogData, LoggerHttpPluginOptions, StoredLogRecord } from './types'

const DEFAULT_MESSAGE_QUEUE_LIMIT = 30
const DEFAULT_BATCH_SIZE = 30
const DEFAULT_SEND_LOGS_INTERVAL_DELAY = 3 * 60 * 1000
const DEFAULT_REQUEST_TIMEOUT = 5_000
const DEFAULT_MAX_STORED_LOGS = 500
const DEFAULT_RETRY_BASE_DELAY = 60_000
const DEFAULT_RETRY_MAX_DELAY = 5 * 60_000

const defaultOptions: LoggerHttpPluginOptions = {
  user: '',
  pass: '',
  apiUrl: '',
  appName: '',
  channel: '',
  messageQueueLimit: DEFAULT_MESSAGE_QUEUE_LIMIT,
  sendLogsIntervalDelay: DEFAULT_SEND_LOGS_INTERVAL_DELAY,
  requestTimeout: DEFAULT_REQUEST_TIMEOUT,
  batchSize: DEFAULT_BATCH_SIZE,
  maxStoredLogs: DEFAULT_MAX_STORED_LOGS,
  retryBaseDelay: DEFAULT_RETRY_BASE_DELAY,
  retryMaxDelay: DEFAULT_RETRY_MAX_DELAY
}

const additionalLogData: LogData = {}
const pendingWrites = new Set<Promise<void>>()

let intervalInstance: number | undefined
let queuedLogsSinceLastFlush = 0
let flushPromise: Promise<void> | undefined
let shouldFlushAgain = false

async function waitForPendingWrites(): Promise<void> {
  if (pendingWrites.size === 0) {
    return
  }

  await Promise.allSettled(Array.from(pendingWrites))
}

const serializeFallback = (source: unknown): unknown => {
  const seen = new WeakSet<object>()
  return JSON.parse(
    JSON.stringify(source, (_key, value: unknown) => {
      if (typeof value === 'function') {
        const functionName = (value as { readonly name?: string }).name || 'anonymous'
        return `[Function ${functionName}]`
      }

      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack
        }
      }

      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]'
        }
        seen.add(value)
      }

      return value
    })
  )
}

const cloneLogData = (source: LogData): LogData => {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(source)
    } catch (err) {
      console.warn('[loggerHttp] Log copy failed. Fallback to JSON serialization', {
        err,
        source
      })
    }
  }

  return serializeFallback(source) as LogData
}

const getLogLevelName = (logLevel: number): string => {
  const foundLevel = Object.entries(log.levels).find(([, value]) => value === logLevel)
  return foundLevel?.[0].toLowerCase() || String(logLevel)
}

const getOption = (value: number | undefined, fallback: number): number => value || fallback

const clearLogsQueue = async (): Promise<void> => {
  await waitForPendingWrites()
  if (flushPromise) {
    await flushPromise
  }

  shouldFlushAgain = false
  queuedLogsSinceLastFlush = 0
  await clearLogRecords()
}

const send = async (recordsToSend: Array<StoredLogRecord>, batchId: string): Promise<void> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    getOption(defaultOptions.requestTimeout, DEFAULT_REQUEST_TIMEOUT)
  )
  const logsToSend = recordsToSend.map(record => ({
    ...record.log,
    batch_id: batchId
  }))

  try {
    const response = await fetch(`${defaultOptions.apiUrl}/bulk_logs`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        Authorization: `Basic ${btoa(`${defaultOptions.user}:${defaultOptions.pass}`)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(logsToSend)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }
  } catch (err) {
    console.error('[loggerHttp] Send logs to graylog error', err)
    throw err
  } finally {
    window.clearTimeout(timeoutId)
  }
}

const flushPersistedLogs = async (): Promise<void> => {
  if (flushPromise) {
    shouldFlushAgain = true
    return flushPromise
  }

  flushPromise = (async () => {
    try {
      await waitForPendingWrites()

      while (true) {
        const records = await getSendableBatch(getOption(defaultOptions.batchSize, DEFAULT_BATCH_SIZE))
        if (records.length === 0) {
          queuedLogsSinceLastFlush = 0
          return
        }

        const batchId = crypto.randomUUID()

        try {
          await send(records, batchId)
          await deleteRecords(records.map(record => record.id))
        } catch {
          await restoreFailedRecords(records, {
            retryBaseDelay: defaultOptions.retryBaseDelay,
            retryMaxDelay: defaultOptions.retryMaxDelay
          })
          return
        }
      }
    } finally {
      flushPromise = undefined
      if (shouldFlushAgain) {
        shouldFlushAgain = false
        flushPersistedLogsAndReportError()
      }
    }
  })()

  return flushPromise
}

const flushPersistedLogsAndReportError = (): void => {
  void flushPersistedLogs().catch(err => {
    console.warn('[loggerHttp] Failed to flush persisted logs', err)
  })
}

const queueLogRecord = (record: StoredLogRecord): void => {
  const persistPromise = (async () => {
    await addLogRecord(record, getOption(defaultOptions.maxStoredLogs, DEFAULT_MAX_STORED_LOGS))
    queuedLogsSinceLastFlush += 1

    if (queuedLogsSinceLastFlush >= getOption(defaultOptions.messageQueueLimit, DEFAULT_MESSAGE_QUEUE_LIMIT)) {
      flushPersistedLogsAndReportError()
    }
  })()

  pendingWrites.add(persistPromise)
  void persistPromise
    .catch(err => {
      console.warn('[loggerHttp] Failed to persist log in IndexedDB', err)
    })
    .finally(() => {
      pendingWrites.delete(persistPromise)
    })
}

const startSendLogsInIntervals = (): void => {
  if (intervalInstance) {
    clearInterval(intervalInstance)
  }

  intervalInstance = window.setInterval(
    () => {
      flushPersistedLogsAndReportError()
    },
    getOption(defaultOptions.sendLogsIntervalDelay, DEFAULT_SEND_LOGS_INTERVAL_DELAY)
  )
}

const apply = (logger: log.Logger, options: LoggerHttpPluginOptions) => {
  const originalFactory = log.methodFactory
  Object.assign(defaultOptions, options)

  startSendLogsInIntervals()
  flushPersistedLogsAndReportError()

  logger.methodFactory = (methodName, logLevel, loggerName) => {
    const rawMethod = originalFactory(methodName, logLevel, loggerName)

    return function (...args) {
      if (log.getLevel() <= logLevel) {
        const now = new Date()
        const logData = cloneLogData({
          message: args[0],
          level: getLogLevelName(logLevel),
          channel: defaultOptions.channel,
          appName: defaultOptions.appName,
          http_user_agent: window.navigator.userAgent,
          timestamp: now.toISOString(),
          tzOffset: -now.getTimezoneOffset() / 60, // offset in hours
          log_id: crypto.randomUUID(),
          ...additionalLogData,
          ...args[1]
        })

        queueLogRecord(createStoredLogRecord(logData))
      }

      rawMethod(...args)
    }
  }

  return logger
}

const sendLogsAndClearLogsQueue = (): Promise<void> => {
  return flushPersistedLogs()
}

const setAdditionalLogData = (newData: Record<string, unknown>) => {
  Object.assign(additionalLogData, newData)
}

export default { apply, clearLogsQueue, sendLogsAndClearLogsQueue, setAdditionalLogData }
